import {
  Button, Collapse, useTheme, useMediaQuery,
} from '@mui/material';
import { useContext, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { AppConstantsContext } from '../../../context';
import { JobType } from '../../../entities';

interface FeatureListProps {
  cardType?: 'free' | 'single' | 'pro' | 'universe' | 'monthly' | 'yearly';
}

const useStyles = makeStyles()((theme) => ({
  item: {
    marginBottom: 8,
    fontSize: 12,
  },
  universe: {
    fontSize: 16,
    marginLeft: '-7px',
  },
  itemUniverse: {
    ' li': {
      margin: 4,
      fontSize: 12,
    },
  },
  list: {
    minHeight: '9.5em',
    paddingInlineStart: '18px',
    [theme.breakpoints.down('md')]: {
      minHeight: 'initial',
    },
  },
  collapse: {
    textTransform: 'none',
  },
}));

const useFeatures = (cardType: 'free' | 'single' | 'pro' | 'universe' | 'monthly' | 'yearly') => {
  const { t } = useTranslation();
  const { jobType } = useContext(AppConstantsContext);

  const getFeatureKey = () => {
    switch (true) {
      case cardType === 'free' && jobType !== JobType.TranscribeScan:
        return 'home.pricing.free_features';
      case cardType === 'universe':
        return 'home.pricing.universe_features';
      case cardType === 'single':
        return 'home.pricing.single_features';
      case jobType === JobType.TranscribeScan && (cardType === 'monthly' || cardType === 'yearly'):
        return 'home.pricing.scan2notes_features';
      case jobType === JobType.TranscribeScan && cardType === 'free':
        return 'home.pricing.scan2notes_features_free';
      default:
        return 'home.pricing.pro_features';
    }
  };

  const features = t(getFeatureKey(), { returnObjects: true });
  const numberOfTranscriptions = jobType === JobType.TranscribeStudio ? t('home.pricing.unlimited') : '50';
  const downloadOptions = ['PDF', 'MIDI', 'Music XML'];

  return {
    features,
    numberOfTranscriptions,
    downloadOptions,
  };
};

const FeatureList: React.FC<FeatureListProps> = ({ cardType }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { classes } = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpen, setIsOpen] = useState(true);
  const { features, numberOfTranscriptions, downloadOptions } = useFeatures(cardType ?? 'pro');

  return (
    <>
      {isMobile && (
        <Collapse in>
          <Button
            className={classes.collapse}
            onClick={() => setIsOpen(!isOpen)}
            endIcon={!isOpen ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          >
            {t('home.pricing.features')}
          </Button>
        </Collapse>
      )}
      {isOpen && (
        <div className={classes.list}>
          {cardType === 'universe' ? (
            <>
              <div
                className={classes.universe}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: t('home.pricing.universe_features'),
                }}
              />
              <div className={classes.itemUniverse}>
                <li>Klangio Transcription Studio</li>
                <li>Piano2Notes</li>
                <li>Guitar2Tabs</li>
                <li>Sing2Notes</li>
                <li>Drum2Notes</li>
                <li>Wind2Notes</li>
                <li>Violin2Notes</li>
              </div>
            </>
          ) : (
            (features as string[]).map((feature: string, index: number) => (
              <li
                className={classes.item}
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: t(feature, {
                    options: downloadOptions.join(', '),
                    transcriptions: numberOfTranscriptions,
                  }),
                }}
              />
            ))
          )}
        </div>
      )}
    </>
  );
};

export default FeatureList;
